import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { saveProjectDraft, type ProjectDraftPayload } from '../api/projects.api';
import { isDraftVersionConflict } from '@/shared/lib/errors';
import { invalidateAfterDraftSave } from '@/shared/lib/invalidate';
import type { Id } from '@/shared/types/domain';

/**
 * Autosave do rascunho (ADR-005 · US-16 · US-17).
 *
 * O contrato com a tela: ela chama `save(patch)` com **só o que mudou**, e este
 * hook decide quando a chamada sai. Dois gatilhos, e a diferença entre eles não
 * é gosto (spec §7.4):
 *
 * - **texto** (`save`, com debounce de 2s): salvar a cada tecla seria uma RPC
 *   por caractere; 2s de silêncio é o fim de uma frase.
 * - **campo discreto** (`saveNow`: checkbox, select, data): sai na hora, porque
 *   marcar "Som" é um evento completo, e esperar 2s por ele só abre janela de
 *   perda.
 *
 * O rascunho nunca vive no navegador (regra 7): não há `localStorage` aqui, e é
 * isso que faz começar no celular e terminar no computador funcionar.
 */

export type AutosaveState = 'idle' | 'saving' | 'saved' | 'conflict' | 'error';

export const AUTOSAVE_DEBOUNCE_MS = 2_000;

interface UseProjectAutosaveOptions {
  /** `null` enquanto o projeto ainda não existe — o primeiro save o cria. */
  projectId: Id | null;
  /** Versão vinda do servidor. `null` = projeto novo. */
  version: number | null;
  /** Chamado uma vez, quando o primeiro save cria a linha. */
  onCreated?: (projectId: Id) => void;
  enabled?: boolean;
}

export interface ProjectAutosave {
  state: AutosaveState;
  error: unknown;
  /** Versão corrente conhecida — sempre a da última resposta do servidor. */
  version: number | null;
  /** Texto: acumula e sai depois de 2s de silêncio. */
  save: (patch: ProjectDraftPayload) => void;
  /** Discreto: sai imediatamente, cancelando o debounce pendente. */
  saveNow: (patch: ProjectDraftPayload) => void;
  /** Garante que tudo que está pendente chegou ao servidor. */
  flush: () => Promise<void>;
  /**
   * Joga fora o que ainda não saiu — e é o oposto exato de `flush`.
   *
   * Existe para UM caso: descartar o rascunho. Todo o resto deste hook
   * conspira para nada se perder (o debounce sai no desmonte, o patch que
   * falhou volta para a fila), e é justamente essa insistência que produziria
   * o pior desfecho aqui — a pessoa confirma "sim, descartar", sai da tela, e
   * a frase que estava em debounce cria de novo o projeto que ela acabou de
   * apagar. Um rascunho ressuscitado é pior que um rascunho perdido: ninguém
   * vai procurá-lo.
   */
  cancel: () => void;
  hasPendingChanges: boolean;
}

export function useProjectAutosave({
  projectId,
  version,
  onCreated,
  enabled = true,
}: UseProjectAutosaveOptions): ProjectAutosave {
  const queryClient = useQueryClient();

  const [state, setState] = useState<AutosaveState>('idle');
  const [error, setError] = useState<unknown>(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const projectIdRef = useRef<Id | null>(projectId);
  const versionRef = useRef<number | null>(version);
  const pendingRef = useRef<ProjectDraftPayload>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const onCreatedRef = useRef(onCreated);

  useEffect(() => {
    onCreatedRef.current = onCreated;
  }, [onCreated]);

  // A tela pode receber o projeto depois (carregamento de uma edição), e a
  // versão muda a cada save — os refs seguem a fonte externa sem re-render.
  useEffect(() => {
    if (projectId) projectIdRef.current = projectId;
  }, [projectId]);
  useEffect(() => {
    if (version !== null) versionRef.current = version;
  }, [version]);

  const send = useCallback(async (): Promise<void> => {
    const patch = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(patch).length === 0) {
      setHasPendingChanges(false);
      return;
    }

    setState('saving');
    try {
      const saved = await saveProjectDraft(
        projectIdRef.current,
        patch,
        // Na criação não há versão a comparar; depois dela, sempre a última do
        // servidor — nunca uma contada no cliente.
        projectIdRef.current ? versionRef.current : null,
      );

      const isCreation = projectIdRef.current === null;
      projectIdRef.current = saved.id;
      versionRef.current = saved.version;

      if (isCreation) onCreatedRef.current?.(saved.id);

      setState('saved');
      setError(null);
      await invalidateAfterDraftSave(queryClient, saved.id);
    } catch (caught) {
      // Devolve o que não foi salvo para a fila: perder a digitação do
      // voluntário por causa de uma falha de rede é o pior desfecho possível.
      pendingRef.current = { ...patch, ...pendingRef.current };
      setError(caught);
      setState(isDraftVersionConflict(caught) ? 'conflict' : 'error');
      throw caught;
    } finally {
      setHasPendingChanges(Object.keys(pendingRef.current).length > 0);
    }
  }, [queryClient]);

  /** Uma chamada por vez: a RPC é otimista por `version`, e duas em paralelo
   *  fariam a segunda nascer com a versão que a primeira ainda vai mudar. */
  const runExclusive = useCallback((): Promise<void> => {
    const previous = inFlightRef.current ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(() => send())
      .catch(() => undefined)
      .finally(() => {
        if (inFlightRef.current === next) inFlightRef.current = null;
      });
    inFlightRef.current = next;
    return next;
  }, [send]);

  const enqueue = useCallback((patch: ProjectDraftPayload) => {
    pendingRef.current = { ...pendingRef.current, ...patch };
    setHasPendingChanges(true);
  }, []);

  const save = useCallback(
    (patch: ProjectDraftPayload) => {
      if (!enabled) return;
      enqueue(patch);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void runExclusive();
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [enabled, enqueue, runExclusive],
  );

  const saveNow = useCallback(
    (patch: ProjectDraftPayload) => {
      if (!enabled) return;
      enqueue(patch);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      void runExclusive();
    },
    [enabled, enqueue, runExclusive],
  );

  const cancel = useCallback((): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = {};
    setHasPendingChanges(false);
    setState('idle');
    setError(null);
  }, []);

  const flush = useCallback(async (): Promise<void> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await runExclusive();
  }, [runExclusive]);

  // Desmontar com debounce pendente perderia a última frase digitada — sair da
  // tela é justamente quando o voluntário acha que já salvou.
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        void runExclusive();
      }
    },
    [runExclusive],
  );

  return {
    state,
    error,
    version: versionRef.current,
    save,
    saveNow,
    flush,
    cancel,
    hasPendingChanges,
  };
}
